import { Box } from '@cowsquare/design';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React from 'react';
import { useQuery } from '@apollo/react-hooks';
import { Helmet } from 'react-helmet';
import { addDays, format } from 'date-fns/esm/fp';
import { startOfToday } from 'date-fns/esm';
import styled from 'styled-components';
import { space } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import { getQueryObject } from '../../../../utils/getQueryObject';
import { getTagClause } from '../../../../utils/getWhereClause';
import { useAdsQuery, useFilterTags, usePagesBindingUrl, useUser } from '../../../../utils/hooks';
import { Advertisements } from '../../../Advertisements';
import { EventCardList } from '../../../EventCardList';
import { FilterTags } from '../../../FilterTags';
import { EventCardListLoading, TagListLoading } from '../../../Loading';
import { Section } from '../../../Section';
import { Title } from '../../../Title';

const PAGE_SIZE = 21;

const SubTitle = styled.h3`
  font-size: ${themeGet('fontSizes.h3')};
  line-height: 1.42;
  font-weight: 600;
  margin: 0;
  ${space};
`;

const Text = styled.span`
  font-size: ${themeGet('fontSizes.h4')};
  color: ${themeGet('colors.blacks.6')};
`;

const GET_UPCOMPING_CME_EVENTS = gql`
  query getUpcomingCMEEvents($doctor_id: uuid!, $where: hkdutest_cme_events_bool_exp!) {
    list: hkdutest_cme_events(order_by: { date: desc }, where: $where) {
      id
      name
      start_time
      end_time
      price
      member_price
      date
      content
      score
      location
      organizer {
        id
        name
      }
      doctors(where: { doctor_id: { _eq: $doctor_id } }) {
        id
      }
      tags {
        tag {
          name
        }
      }
    }
  }
`;

const GET_CME_EVENTS = gql`
  query getCMEEvents($limit: Int, $offset: Int, $doctor_id: uuid!, $where: hkdutest_cme_events_bool_exp) {
    tags: hkdutest_cme_event_tags {
      name
    }
    total: hkdutest_cme_events_aggregate(where: $where) {
      aggregate {
        count
      }
    }
    list: hkdutest_cme_events(limit: $limit, order_by: { date: desc }, offset: $offset, where: $where) {
      id
      name
      start_time
      end_time
      price
      member_price
      date
      content
      score
      location
      organizer {
        id
        name
      }
      doctors(where: { doctor_id: { _eq: $doctor_id } }) {
        id
      }
      tags {
        tag {
          name
        }
      }
    }
  }
`;

const EventList = ({ location, uri }) => {
  const queryObject = getQueryObject(location);
  const [page, onPageChange] = usePagesBindingUrl(queryObject, uri);
  const [tag, handleTagChange] = useFilterTags(queryObject, uri);
  const { ads } = useAdsQuery(uri);
  const whereClause = getTagClause(tag);
  const { HKDUUserInfo } = useUser();
  const doctorId = R.pathOr(null, ['doctor', 'id'], HKDUUserInfo);
  // IOSString 有時區問題
  const todayDateString = format('y-M-dd', startOfToday());
  const { data: upcomingEventsData } = useQuery(GET_UPCOMPING_CME_EVENTS, {
    skip: !doctorId,
    variables: {
      doctor_id: doctorId,
      where: {
        date: { _gte: todayDateString, _lte: addDays(7, new Date(todayDateString)) },
      },
    },
  });
  const { data: eventsData, loading } = useQuery(GET_CME_EVENTS, {
    skip: !doctorId,
    variables: {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      doctor_id: doctorId,
      where: {
        ...whereClause,
        publish_date: { _lte: todayDateString },
        date: { _gte: todayDateString },
      },
    },
  });

  const list = R.propOr([], 'list', eventsData);
  const upcomingEventList = R.propOr([], 'list', upcomingEventsData);
  const tags = R.pipe(
    R.propOr([], 'tags'),
    R.pluck('name'),
    R.unless(R.isEmpty, R.prepend('All'))
  )(eventsData);
  const total = R.pathOr(0, ['total', 'aggregate', 'count'], eventsData);

  return (
    <Section>
      <Helmet>
        <title>CME Events - HKDU CME</title>
      </Helmet>
      <Advertisements ads={ads} />
      <Box px={[5, 0]} py={8}>
        <Box mb={7}>
          <Title m={0} mb={[5, 0]}>
            CME Events
          </Title>
        </Box>
        <Box mb={5}>
          <SubTitle> Upcoming CME Events </SubTitle>
        </Box>
        <Box pb={8}>
          {R.and(R.isEmpty(upcomingEventList), loading) && <EventCardListLoading />}
          {R.and(R.isEmpty(upcomingEventList), !loading) && (
            <Text>No upcoming events in next 7 days. Check all CME event for more!</Text>
          )}
          <EventCardList
            uri={uri}
            list={upcomingEventList}
            options={() => ({
              dateFormat: 'ccc, dd-MMM-yyyy',
            })}
          />
        </Box>
        <Box mb={5}>
          <SubTitle> CME Events </SubTitle>
        </Box>
        <Box mb={6}>
          {R.and(R.isEmpty(tags), loading) && <TagListLoading />}
          <FilterTags value={tag} tags={tags} onChange={handleTagChange} />
        </Box>
        <Box pb={8}>
          {R.and(R.isEmpty(list), loading) && <EventCardListLoading />}
          <EventCardList
            uri={uri}
            page={page}
            pageSize={PAGE_SIZE}
            onPageChange={onPageChange}
            list={list}
            total={total}
            options={() => ({
              dateFormat: 'ccc, dd-MMM-yyyy',
            })}
          />
        </Box>
      </Box>
    </Section>
  );
};

export default EventList;
